import { logSeMudou } from '~/utils/logSeMudou';
import { makeAutoObservable, runInAction } from 'mobx';
import {
  Campaign,
  PlayerGroupSchedule,
  PlayerSchedule,
  PlaylistItem,
  PlaylistSchedule,
  ScheduleChangeCallback,
  ISchedulerService,
} from '~/services/scheduler/types';
import { getAllActiveCampaignsSorted } from '~/services/scheduler/utils/campaignMatcher';
import {
  isContentPlayable,
  isContentValidNow,
} from '~/services/scheduler/utils/contentScheduleFilter';
import { isGroupSchedulePlayable } from '~/services/scheduler/utils/groupScheduleFilter';
import { isPlayerCurrentlyOpen } from '~/services/scheduler/utils/playerScheduleFilter';
import { isPlaylistSchedulePlayable } from '~/services/scheduler/utils/playlistScheduleFilter';
import { prewarmScheduleFormatter } from '~/services/scheduler/utils/scheduleParser';
import { matchesWeather } from '~/services/scheduler/utils/weatherConditionMatcher';
import { weatherStore } from '~/stores/weather.store';

// v1.0.42 — re-evaluation rápida pra detectar transições de janela horária
// (caso real: campanha A expira à meia-noite, campanha B vira ativa às 00:00).
// Antes era 60s — gap de até 60s entre fim de A e detecção de B. Resultado:
// player ficava preso em "Aguardando conteúdo" até o próximo sync `yes` (que
// nem sempre vem rápido se o server cacheia hash).
//
// 15s é um sweet spot: detecta transições rápido sem custo significativo
// (getAllActiveCampaignsSorted é filter sobre array pequeno — <1ms).
const EVALUATION_INTERVAL_MS = 15000; // Re-evaluate every 15s
const CAMPAIGNS_STORAGE_KEY = 'efflux_cached_campaigns';
const FALLBACK_STORAGE_KEY = 'efflux_cached_fallback';

class SchedulerStore implements ISchedulerService {
  // Observable state
  campaigns: Campaign[] = [];
  fallbackPlaylist: PlaylistItem[] = [];
  activeCampaign: Campaign | null = null;
  isInitialized: boolean = false;
  lastEvaluatedAt: Date | null = null;
  /**
   * V060 — Janelas dos grupos a que o player pertence. Setado pelo
   * SyncStore após `transformSyncResponse`. Lido em
   * `filterPlaylistBySchedule` pra compor a 4ª camada AND
   * (Conteúdo ∩ PlaylistItem ∩ Campanha ∩ Grupo).
   */
  playerGroups: PlayerGroupSchedule[] = [];

  /**
   * V066 — Janela do PRÓPRIO player. Single object (player tem 1 schedule).
   * Lido em `filterPlaylistBySchedule` pra fazer EARLY RETURN se janela
   * fechada agora — 5ª camada AND com prioridade (mais barato que
   * processar 4 outras camadas).
   *
   * NULL = sem schedule (player liberado 24/7 — back-compat absoluta).
   * Sem bypass (diferente do Grupo V064): janela aplica a TUDO.
   */
  playerSchedule: PlayerSchedule | null = null;

  /**
   * V067 — Janelas das playlists de origem dos itens. Setado pelo SyncStore
   * após `transformSyncResponse`. Lido em `filterPlaylistBySchedule` pra
   * compor a 6ª camada AND (Conteúdo ∩ PlaylistItem ∩ Campanha ∩ Grupo ∩
   * Player ∩ Playlist). UNIÃO via `viaPlaylists`; itens sem `viaPlaylists`
   * (anexas) fazem BYPASS. Espelha `playerGroups` (V060).
   */
  playlistSchedules: PlaylistSchedule[] = [];

  /**
   * MEMOIZAÇÃO (Renato 2026-07-17) — cache do resultado de `computeActivePlaylist`.
   * O filtro das 6 camadas AND sobre N itens (~300ms com 684) NÃO pode rodar a cada
   * leitura — era o freeze de até 20s (dezenas de recomputes por transição, porque o
   * computed lia `new Date()` e nunca cacheava). Recomputado 1× por `evaluateSchedule`
   * (tick de 15s + todo setter de input via evaluateSchedule); as leituras (getter
   * `activePlaylist`, `getActivePlaylist`, observers, sync.store) retornam ESTE cache.
   * Observable → observers re-renderizam só quando o resultado realmente muda.
   */
  private _activePlaylistCache: PlaylistItem[] = [];

  // Internal (non-observable)
  private evaluationInterval: ReturnType<typeof setInterval> | null = null;
  private changeCallbacks: Set<ScheduleChangeCallback> = new Set();
  private lastPlaylistWasEmpty: boolean = true;

  constructor() {
    // changeCallbacks excluído da observabilidade — MobX wrap o Set
    // numa ObservableSet, que (in)conscientemente wrap as funções no .add().
    // Resultado: o unsubscribe closure (que chama .delete com o callback
    // original) não encontra o item dentro do ObservableSet.
    // MobX AnnotationsMap não suporta exclusão por nome de propriedade
    // não-existente no tipo (changeCallbacks é private). Cast necessário.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    makeAutoObservable(this, { changeCallbacks: false } as any);
    // Warm the Intl.DateTimeFormat cache off the critical path, so the first
    // schedule evaluation (below, or the first sync) doesn't pay the one-time
    // ICU/tz-database load on Tizen. See scheduleParser.formatterCache.
    prewarmScheduleFormatter();
    // Load cached campaigns from previous session (offline-first)
    this.loadFromCache();
  }

  /**
   * Load campaigns from localStorage (offline-first behavior).
   * This allows playback even when server is unreachable.
   */
  private loadFromCache(): void {
    try {
      const cachedCampaigns = localStorage.getItem(CAMPAIGNS_STORAGE_KEY);
      const cachedFallback = localStorage.getItem(FALLBACK_STORAGE_KEY);

      if (cachedCampaigns) {
        const campaigns = JSON.parse(cachedCampaigns);
        console.log(
          `[SchedulerStore] 📦 Loaded ${campaigns.length} campaigns from cache`,
        );
        runInAction(() => {
          this.campaigns = campaigns;
          this.isInitialized = true;
        });
        this.evaluateSchedule();
      }

      if (cachedFallback) {
        const fallback = JSON.parse(cachedFallback);
        console.log(
          `[SchedulerStore] 📦 Loaded ${fallback.length} fallback items from cache`,
        );
        runInAction(() => {
          this.fallbackPlaylist = fallback;
        });
      }
    } catch (error) {
      console.warn('[SchedulerStore] Failed to load from cache:', error);
    }
  }

  /**
   * Save campaigns to localStorage for offline playback.
   */
  private saveToCache(): void {
    try {
      localStorage.setItem(
        CAMPAIGNS_STORAGE_KEY,
        JSON.stringify(this.campaigns),
      );
      localStorage.setItem(
        FALLBACK_STORAGE_KEY,
        JSON.stringify(this.fallbackPlaylist),
      );
      console.debug(
        `[SchedulerStore] 💾 Saved ${this.campaigns.length} campaigns and ${this.fallbackPlaylist.length} fallback items to cache`,
      );
    } catch (error) {
      console.warn('[SchedulerStore] Failed to save to cache:', error);
    }
  }

  // ===========================================================================
  // COMPUTED PROPERTIES
  // ===========================================================================

  /**
   * Returns the active playlist with ALL active campaigns rotated sequentially.
   * Each campaign's playlist is played in full, then moves to the next campaign.
   * Campaigns are ordered by priority (0 = highest priority first).
   */
  get activePlaylist(): PlaylistItem[] {
    // MEMOIZAÇÃO (Renato 2026-07-17): retorna o cache computado por evaluateSchedule.
    // NÃO recomputa aqui — o filtro caro (~300ms/684) roda 1× por tick/setter, não por
    // leitura. Ver `computeActivePlaylist` + `_activePlaylistCache`.
    return this._activePlaylistCache;
  }

  /**
   * Computa a activePlaylist do ZERO — filtro das 6 camadas AND (Conteúdo ∩
   * PlaylistItem ∩ Campanha ∩ Grupo ∩ Player ∩ Playlist) + fallback + emergência (R5).
   * CARO (~300ms com 684 itens). NÃO chamar em leitura — SÓ via `evaluateSchedule`
   * (memoização, Renato 2026-07-17). Corpo IDÊNTICO ao getter antigo — comportamento
   * 100% preservado (R1 mínimos, R3 Bresenham no server, R0-R5 temporais, R5 emergência).
   */
  private computeActivePlaylist(): PlaylistItem[] {
    const now = new Date();
    // R5 (2026-06-20) — a campanha de emergência (isEmergencyFallback) NUNCA
    // participa do loop regular: é last-resort. Removida ANTES de
    // getAllActiveCampaignsSorted pra não double-play quando há campanhas
    // regulares ativas. A emergencial associada DIRETO ao player vem SEM a
    // flag do server (toca normal — R2), então não é filtrada aqui.
    const regularCampaigns = this.campaigns.filter(
      (c) => c.isEmergencyFallback !== true,
    );
    const activeCampaigns = getAllActiveCampaignsSorted(regularCampaigns, now);

    // RUÍDO (2026-10-15): aqui despejava a avaliação inteira a CADA ciclo e, logo abaixo,
    // uma linha por campanha — 11 linhas a cada 15s, sempre iguais. O diagnóstico que isto
    // servia (playlist vazia com itens válidos no server, Tizen v1.0.89) depende da
    // TRANSIÇÃO, não da repetição: o resumo no fim do método sai quando o resultado muda,
    // e os caminhos de playlist vazia seguem com `console.warn` próprio.

    if (activeCampaigns.length === 0) {
      console.warn(
        '[SchedulerStore] ⚠️ Zero active campaigns. Falling back. Total stored:',
        this.campaigns.length,
      );
      const regularFallback = this.filterPlaylistBySchedule(
        this.fallbackPlaylist,
      );
      if (regularFallback.length > 0) {
        return regularFallback;
      }
      return this.emergencyFallbackPlaylist(now);
    }

    // Combine all campaign playlists sequentially
    const combinedPlaylist: PlaylistItem[] = [];

    const porCampanha: string[] = [];
    activeCampaigns.forEach((campaign) => {
      const filteredItems = this.filterPlaylistBySchedule(campaign.playlist);
      porCampanha.push(
        `${campaign.id}:${campaign.playlist.length}→${filteredItems.length}`,
      );
      combinedPlaylist.push(...filteredItems);
    });

    // When all campaign items are filtered by time window (e.g. schedule expired),
    // fall back to default content instead of leaving the player blank.
    if (combinedPlaylist.length === 0) {
      console.warn(
        '[SchedulerStore] ⚠️ All items filtered out by schedule. Falling back to default. Sample item:',
        JSON.stringify(activeCampaigns[0].playlist[0]),
      );
      const regularFallback = this.filterPlaylistBySchedule(
        this.fallbackPlaylist,
      );
      if (regularFallback.length > 0) {
        return regularFallback;
      }
      return this.emergencyFallbackPlaylist(now);
    }

    logSeMudou(
      'scheduler.activePlaylist',
      `[SchedulerStore] playlist: ${combinedPlaylist.length} itens de ` +
        `${activeCampaigns.length}/${this.campaigns.length} campanhas ` +
        `[${porCampanha.join(' ')}]`,
    );
    return combinedPlaylist;
  }

  /**
   * R5 (2026-06-20) — Campanha de emergência ("nunca sem tocar").
   *
   * Consultada SOMENTE quando a `activePlaylist` regular ficou vazia (último
   * recurso). Procura a campanha marcada `isEmergencyFallback` e devolve a
   * playlist dela com **bypass 24/7 de janela/dia**: NÃO aplica
   * `filterPlaylistBySchedule` (campanha/playlist/grupo/player) NEM os filtros
   * de dia/horário do conteúdo. Aplica APENAS o corte de validade
   * (`isContentValidNow` — `validUntil`/`validFrom` no fuso LOCAL): conteúdo
   * vencido NÃO toca mesmo na emergência (R4 intacta). Se tudo vencido →
   * `[]` → NoContent.
   *
   * Vide regras-temporais-player.md R5.
   */
  private emergencyFallbackPlaylist(now: Date): PlaylistItem[] {
    const emergency = this.campaigns.find(
      (c) => c.isEmergencyFallback === true,
    );
    if (!emergency) {
      console.warn(
        '[SchedulerStore] activePlaylist vazio e SEM campanha de emergência → NoContent',
      );
      return [];
    }

    // 24/7 bypass: só o corte de validade (validUntil/validFrom). Sem
    // janela/dia (campanha/playlist/grupo/player nem daysOfWeek/startAt/endAt
    // do conteúdo). validUntil é hard expiry — não toca conteúdo vencido.
    const items = emergency.playlist.filter(
      (item) => isContentValidNow(item, now).isPlayable,
    );

    if (items.length === 0) {
      console.warn(
        '[SchedulerStore] campanha de emergência sem conteúdo válido (tudo expirado/validUntil) → NoContent',
      );
      return [];
    }

    console.warn(
      `[SchedulerStore] activePlaylist vazio → usando campanha de emergência (isEmergencyFallback): ${items.length} itens válidos (bypass 24/7, só validUntil)`,
    );
    return items;
  }

  /**
   * V088 (2026-08-08) — Procura um PlaylistItem por contentId nas campanhas
   * ativas. Usado pelo trigger.store pra enriquecer o payload do gatilho
   * (que só carrega dados mínimos) com weather + validade + dia/hora do
   * conteúdo antes de passar por `filterPlaylistItemsBySchedule`.
   *
   * <p>Retorna o primeiro match encontrado (todas as campanhas devem ter os
   * mesmos metadados do CONTEÚDO — vide guardrail 2026-06-18 sobre não
   * sobrescrever `daysOfWeek/validFrom/validUntil` do conteúdo na
   * `applyProgramacaoOverrides`).
   *
   * <p>Retorna `null` se o conteúdo não estiver em nenhuma campanha ativa
   * (best-effort: trigger passa direto sem enriquecer).
   */
  findPlaylistItemByContentId(contentId: string): PlaylistItem | null {
    for (const campaign of this.campaigns) {
      const item = campaign.playlist?.find((p) => p.id === contentId);
      if (item) return item;
    }
    return null;
  }

  /**
   * V088 (2026-08-08) — Wrapper público pra `filterPlaylistBySchedule`.
   * Usado pelo `sync.store.applyPlaylistFromPlaybackStore` (Tier 2/3) pra
   * aplicar as MESMAS 6 camadas AND que o Tier 4 usa: Conteúdo ∩ PlaylistItem
   * ∩ Campanha ∩ Grupo ∩ Player ∩ Playlist + weather do CONTEÚDO.
   *
   * <p>Antes desta refação, o Tier 2/3 escapava do filtro completo — só a
   * regra de clima era aplicada em `sync.store`, e mesmo assim os campos
   * chegavam undefined porque o server não os propagava no `Slot` DTO.
   *
   * <p>Regras-temporais-player.md R2/R4.
   */
  filterPlaylistItemsBySchedule(items: PlaylistItem[]): PlaylistItem[] {
    return this.filterPlaylistBySchedule(items);
  }

  /**
   * Filters playlist items by current time and day of week.
   *
   * V059 — quando o item tem `scheduleMode='ADVANCED'` (ou
   * `weeklySchedule`), delega pra `isContentPlayable` em
   * `contentScheduleFilter.ts` (que reusa `isScheduleActive` e a
   * normalização Tizen hour=24). Items SIMPLE/legacy seguem o caminho
   * antigo — comportamento idêntico ao histórico.
   */
  private filterPlaylistBySchedule(items: PlaylistItem[]): PlaylistItem[] {
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const dayMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const currentDay = dayMap[now.getDay()];

    // V066 — 5ª camada AND (janela do PRÓPRIO player). Early return se o
    // player está fechado AGORA — mais barato que processar as 4 outras
    // camadas (Conteúdo/Campanha/PlaylistItem/Grupo).
    // Sem bypass — diferente do Grupo (V064): janela aplica a TUDO inclusive
    // associação direta. R1 do guardrail (tab_stores não influencia)
    // MANTIDA — esta é configuração explícita do próprio player.
    const playerDec = isPlayerCurrentlyOpen(this.playerSchedule, now);
    if (!playerDec.isPlayable) {
      console.debug(
        `[SchedulerStore] V066 EARLY RETURN: player fora da janela: ${playerDec.reason}. ` +
          `Playlist zerada (${items.length} items descartados pré-filtro).`,
      );
      return [];
    }

    let dayFiltered = 0;
    let timeFiltered = 0;
    let advancedFiltered = 0;
    let groupFiltered = 0; // V060 — 4ª camada AND (janela do grupo)
    let playlistFiltered = 0; // V067 — 6ª camada AND (janela da playlist)

    const filtered = items.filter((item) => {
      // V060 — 4ª camada AND: janela do(s) grupo(s) com bypass via direto e
      // UNIÃO multi-grupo. Aplicado ANTES do filtro de Conteúdo/PlaylistItem
      // pra que a razão de bloqueio mais comum (e mais "alta no estrato")
      // ganhe prioridade no log de diagnóstico. Não muda o resultado final
      // (AND é comutativo) — só diagnóstico.
      const groupDec = isGroupSchedulePlayable(item, this.playerGroups, now);
      if (!groupDec.isPlayable) {
        groupFiltered++;
        console.debug(
          `[SchedulerStore] item "${item.name}" (id=${item.id}) filtrado por grupo: ${groupDec.reason}`,
        );
        return false;
      }

      // V067 — 6ª camada AND: janela da(s) playlist(s) de origem com bypass
      // via anexa (sem viaPlaylists) e UNIÃO multi-playlist. Composta ao lado
      // do filtro de Grupo (AND é comutativo) — só diagnóstico decide a ordem.
      const playlistDec = isPlaylistSchedulePlayable(
        item,
        this.playlistSchedules,
        now,
      );
      if (!playlistDec.isPlayable) {
        playlistFiltered++;
        console.debug(
          `[SchedulerStore] item "${item.name}" (id=${item.id}) filtrado por playlist: ${playlistDec.reason}`,
        );
        return false;
      }

      // V088 (2026-08-07) — regra de clima do CONTEÚDO (AND) roda ANTES do
      // gate SIMPLE/ADVANCED pra cobrir os 2 caminhos. Sem esse check,
      // conteúdo SIMPLE com só `weatherConditionContent` setado escapava do
      // filtro e piscava no player antes de ser removido no próximo tick.
      // regras-temporais-player.md R2/R4.
      if (item.weatherConditionContent) {
        if (
          !matchesWeather(item.weatherConditionContent, weatherStore.current)
        ) {
          advancedFiltered++;
          console.debug(
            `[SchedulerStore] item "${item.name}" (id=${item.id}) filtrado por clima do conteúdo`,
          );
          return false;
        }
      }

      // V088 (2026-08-08) — validade do CONTEÚDO (validFrom/validUntil) roda
      // ANTES do gate SIMPLE/ADVANCED pra cobrir os 2 caminhos. O ADVANCED já
      // faz esse check via `isContentPlayable → isContentValidNow`, mas o
      // SIMPLE só filtrava dia/hora — validade ficava delegada à margem
      // assimétrica de ~2h do server. Cliente aplica no fuso LOCAL do device.
      // regras-temporais-player.md R0/R4.
      const validity = isContentValidNow(item, now);
      if (!validity.isPlayable) {
        advancedFiltered++;
        console.debug(
          `[SchedulerStore] item "${item.name}" (id=${item.id}) filtrado por validade: ${validity.reason}`,
        );
        return false;
      }

      // V059 — ADVANCED mode usa weeklySchedule (per-day window).
      // Delega pra isContentPlayable que reusa scheduleParser
      // (preserva normalização Tizen hour=24).
      if (item.scheduleMode === 'ADVANCED' || item.weeklySchedule) {
        const decision = isContentPlayable(item, now);
        if (!decision.isPlayable) {
          advancedFiltered++;
          console.debug(
            `[SchedulerStore] item "${item.name}" (id=${item.id}) filtrado: ${decision.reason}`,
          );
          return false;
        }
        return true;
      }

      // SIMPLE/legacy — caminho existente (intocado).
      // Filter by day of week (empty = all days)
      if (item.daysOfWeek && item.daysOfWeek.length > 0) {
        if (!item.daysOfWeek.includes(currentDay)) {
          dayFiltered++;
          return false;
        }
      }

      // Filter by time range
      const startAt = item.startAt ?? '00:00';
      const endAt = item.endAt ?? '23:59';

      // Handle time comparison (simple string comparison works for HH:MM format)
      if (currentTime < startAt || currentTime > endAt) {
        timeFiltered++;
        return false;
      }

      return true;
    });

    if (groupFiltered > 0) {
      console.debug(
        `[SchedulerStore] filterPlaylistBySchedule: ${groupFiltered} items filtrados por janela de Grupo (V060)`,
      );
    }

    if (playlistFiltered > 0) {
      console.debug(
        `[SchedulerStore] filterPlaylistBySchedule: ${playlistFiltered} items filtrados por janela de Playlist (V067)`,
      );
    }

    // Capture advancedFiltered count for diagnostic log below (preserva contrato)
    if (advancedFiltered > 0) {
      console.debug(
        `[SchedulerStore] filterPlaylistBySchedule: ${advancedFiltered} items filtrados por scheduleMode=ADVANCED/weeklySchedule`,
      );
    }

    if (items.length > 0 && filtered.length === 0) {
      // Diagnostic: when ALL items are filtered out, log a sample so
      // we can spot bad data (e.g. days_of_week as strings vs numbers,
      // startAt/endAt formato errado, currentTime no Tizen incorreto).
      console.warn(
        '[SchedulerStore] 🚨 filterPlaylistBySchedule ZEROU items:',
        JSON.stringify({
          totalItems: items.length,
          dayFiltered,
          timeFiltered,
          currentTime,
          currentDay,
          sampleItem: {
            id: items[0].id,
            name: items[0].name,
            daysOfWeek: items[0].daysOfWeek,
            startAt: items[0].startAt,
            endAt: items[0].endAt,
          },
        }),
      );
    }

    return filtered;
  }

  /**
   * Indicates whether fallback content is being used.
   */
  get isUsingFallback(): boolean {
    return this.activeCampaign === null && this.fallbackPlaylist.length > 0;
  }

  /**
   * Indicates whether there's any content to play.
   */
  get hasContent(): boolean {
    return this.activePlaylist.length > 0;
  }

  /**
   * Returns the name of the active campaign or "Fallback" if using fallback.
   */
  get activeCampaignName(): string {
    if (this.activeCampaign) {
      return this.activeCampaign.name;
    }
    return this.isUsingFallback ? 'Fallback' : 'None';
  }

  /**
   * Returns count of all campaigns.
   */
  get totalCampaigns(): number {
    return this.campaigns.length;
  }

  // ===========================================================================
  // INTERFACE METHODS (ISchedulerService)
  // ===========================================================================

  getActiveCampaign(): Campaign | null {
    return this.activeCampaign;
  }

  getActivePlaylist(): PlaylistItem[] {
    return this.activePlaylist;
  }

  /**
   * True quando há ao menos uma campanha regular (não emergência) ativa
   * agora — janela horária, grupo e validade. Usado pelo SyncStore pra
   * decidir se slot_sequence (Tier 2/3) ou scheduler (fallback/emergência R5).
   */
  hasActiveRegularCampaigns(now: Date = new Date()): boolean {
    const regularCampaigns = this.campaigns.filter(
      (c) => c.isEmergencyFallback !== true,
    );
    return getAllActiveCampaignsSorted(regularCampaigns, now).length > 0;
  }

  onScheduleChange(callback: ScheduleChangeCallback): () => void {
    this.changeCallbacks.add(callback);
    return () => {
      this.changeCallbacks.delete(callback);
    };
  }

  setCampaigns(campaigns: Campaign[]): void {
    runInAction(() => {
      // Filter to only active status campaigns
      this.campaigns = campaigns.filter((c) => c.status === 'active');
      this.isInitialized = true;
    });

    // Save to cache for offline playback
    this.saveToCache();

    // Re-evaluate which campaign is active
    this.evaluateSchedule();
  }

  setFallbackPlaylist(playlist: PlaylistItem[]): void {
    runInAction(() => {
      this.fallbackPlaylist = playlist;
    });

    // Save to cache for offline playback
    this.saveToCache();

    // MEMOIZAÇÃO (Renato 2026-07-17): o fallback muda o resultado da activePlaylist
    // quando não há campanha ativa → precisa recomputar o cache. evaluateSchedule
    // refaz o cache + notifica os listeners (emptinessChanged quando o fallback
    // preenche uma playlist antes vazia). Antes: só notifyListeners (sem recompute) —
    // agora as leituras retornam cache, então o recompute tem que ser explícito.
    this.evaluateSchedule();
  }

  /**
   * V060 — Atualiza janelas dos grupos a que o player pertence. Chamado
   * pelo SyncStore após `transformSyncResponse`. Re-avalia o schedule
   * pra propagar imediatamente se algum grupo virou aberto/fechado
   * desde o último sync.
   */
  setPlayerGroups(groups: PlayerGroupSchedule[]): void {
    runInAction(() => {
      this.playerGroups = groups;
    });
    this.evaluateSchedule();
  }

  /**
   * V066 — Setter pra janela do próprio player. Single object (não lista).
   * NULL = liberado 24/7 (back-compat absoluta). Setado pelo SyncStore após
   * `transformSyncResponse`. Re-avalia o schedule pra propagar imediatamente
   * se o player virou aberto/fechado desde o último sync (ex: admin mudou
   * janela enquanto player estava conectado).
   */
  setPlayerSchedule(schedule: PlayerSchedule | null): void {
    runInAction(() => {
      this.playerSchedule = schedule;
    });
    this.evaluateSchedule();
  }

  /**
   * V067 — Atualiza as janelas das playlists de origem dos itens. Chamado pelo
   * SyncStore após `transformSyncResponse`. Re-avalia o schedule pra propagar
   * imediatamente se alguma playlist virou aberta/fechada desde o último sync.
   * Espelha `setPlayerGroups` (V060).
   */
  setPlaylistSchedules(schedules: PlaylistSchedule[]): void {
    runInAction(() => {
      this.playlistSchedules = schedules;
    });
    this.evaluateSchedule();
  }

  // ===========================================================================
  // SCHEDULE EVALUATION
  // ===========================================================================

  /**
   * Re-evaluates active campaigns and playlist based on current time.
   * Now supports multiple active campaigns running sequentially.
   */
  evaluateSchedule(): void {
    const now = new Date();
    const activeCampaigns = getAllActiveCampaignsSorted(this.campaigns, now);
    const firstCampaign =
      activeCampaigns.length > 0 ? activeCampaigns[0] : null;

    const previousCampaignId = this.activeCampaign?.id;
    const newCampaignId = firstCampaign?.id;
    const campaignChanged = previousCampaignId !== newCampaignId;
    const previousPlaylistEmpty = this.lastPlaylistWasEmpty;

    // MEMOIZAÇÃO (Renato 2026-07-17): recomputa a activePlaylist AQUI (1× por tick de
    // 15s + todo setter de input, que chamam evaluateSchedule) e guarda no cache. As
    // leituras retornam o cache → elimina o recompute de ~300ms por leitura (freeze 20s).
    const computedPlaylist = this.computeActivePlaylist();

    runInAction(() => {
      this.activeCampaign = firstCampaign;
      this.lastEvaluatedAt = now;
      this._activePlaylistCache = computedPlaylist;
    });

    const currentPlaylistEmpty = computedPlaylist.length === 0;
    const emptinessChanged = previousPlaylistEmpty !== currentPlaylistEmpty;
    this.lastPlaylistWasEmpty = currentPlaylistEmpty;

    if (campaignChanged || activeCampaigns.length > 1 || emptinessChanged) {
      logSeMudou(
        'scheduler.playlistUpdated',
        `[SchedulerStore] campanhas ativas: ${activeCampaigns.length} (vazia=${currentPlaylistEmpty})`,
      );
      this.notifyListeners(firstCampaign);
    }
  }

  /**
   * Notifies all registered callbacks about campaign change.
   */
  private notifyListeners(campaign: Campaign | null): void {
    this.changeCallbacks.forEach((callback) => {
      try {
        callback(campaign);
      } catch (error) {
        console.error(
          '[SchedulerStore] Error in schedule change callback:',
          error,
        );
      }
    });
  }

  // ===========================================================================
  // LIFECYCLE
  // ===========================================================================

  /**
   * Starts periodic schedule evaluation.
   * Should be called when the app is ready to play content.
   */
  startEvaluation(): void {
    this.stopEvaluation();

    // Immediate evaluation
    this.evaluateSchedule();

    // Set up periodic re-evaluation
    this.evaluationInterval = setInterval(() => {
      this.evaluateSchedule();
    }, EVALUATION_INTERVAL_MS);

    console.debug(
      `[SchedulerStore] Started schedule evaluation (every ${EVALUATION_INTERVAL_MS / 1000}s)`,
    );
  }

  /**
   * Stops periodic schedule evaluation.
   * Should be called when the app is backgrounded or shutting down.
   */
  stopEvaluation(): void {
    if (this.evaluationInterval) {
      clearInterval(this.evaluationInterval);
      this.evaluationInterval = null;
      console.debug('[SchedulerStore] Stopped schedule evaluation');
    }
  }

  /**
   * Resets the store to initial state.
   */
  reset(): void {
    this.stopEvaluation();

    runInAction(() => {
      this.campaigns = [];
      this.fallbackPlaylist = [];
      this.activeCampaign = null;
      this.isInitialized = false;
      this.lastEvaluatedAt = null;
    });

    this.changeCallbacks.clear();
  }

  // ===========================================================================
  // DEBUG / DEV HELPERS
  // ===========================================================================

  /**
   * Logs current state for debugging.
   */
  debugState(): void {
    console.group('[SchedulerStore] Debug State');
    console.log('Total campaigns:', this.totalCampaigns);
    console.log('Active campaign:', this.activeCampaign?.name || 'none');
    console.log('Active playlist items:', this.activePlaylist.length);
    console.log('Is using fallback:', this.isUsingFallback);
    console.log('Last evaluated:', this.lastEvaluatedAt?.toISOString());
    console.log('Listeners count:', this.changeCallbacks.size);
    console.groupEnd();
  }

  /**
   * Forces a specific campaign to be active (for testing).
   * Bypasses normal schedule evaluation.
   */
  forceActiveCampaign(campaignId: string | null): void {
    if (campaignId === null) {
      runInAction(() => {
        this.activeCampaign = null;
      });
      this.notifyListeners(null);
      return;
    }

    const campaign = this.campaigns.find((c) => c.id === campaignId);
    if (campaign) {
      runInAction(() => {
        this.activeCampaign = campaign;
      });
      this.notifyListeners(campaign);
    } else {
      console.warn(`[SchedulerStore] Campaign not found: ${campaignId}`);
    }
  }
}

export default SchedulerStore;
